// src/KioskApp.jsx - Modalità kiosk con notifiche migliorate
import React, { useState, useEffect, useRef } from 'react';
import TimekeepingScanner from './components/TimekeepingScanner';
import Notification from './components/Notification';
import KioskLockOverlay from './components/KioskLockOverlay';
import timekeepingService from './services/timekeepingService';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
import { BUILD_TIMESTAMP } from './utils/version';
import {
  clearBuildCaches,
  canAttemptUpdate,
  recordUpdateAttempt,
  clearUpdateAttemptsIfUpToDate,
  markRelockPending,
  isRelockPending,
  clearRelockPending
} from './utils/kioskUpdate';
import {
  activateKioskLock,
  deactivateKioskLock,
  requestKioskFullscreen,
  exitKioskFullscreen,
  isKioskLocked,
  allowNextUnload
} from './utils/kioskLock';
import './kioskMode.css';

// Ogni quanto il kiosk (sempre acceso) controlla se è stato pubblicato un
// nuovo deploy. 5 minuti è un buon compromesso tra reattività e carico.
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Un aggiornamento trovato viene applicato solo quando il kiosk è inattivo da
// almeno questo tempo, così nessun dipendente si vede ricaricare la pagina
// mentre sta timbrando.
const UPDATE_IDLE_MS = 60 * 1000;
const UPDATE_IDLE_CHECK_MS = 15 * 1000;

/**
 * Applicazione in modalità kiosk per dispositivi Android dedicati
 * Con sistema di notifiche migliorato
 */
const KioskApp = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '', size: 'normal' });
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [emergencyCode, setEmergencyCode] = useState('');
  const [showEmergencyInput, setShowEmergencyInput] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [tapTimer, setTapTimer] = useState(null);
  // Stato del blocco a schermo intero (vedi utils/kioskLock.js)
  const [isLocked, setIsLocked] = useState(() => isKioskLocked());
  // Se il dispositivo rifiuta il fullscreen smettiamo di insistere: meglio un
  // kiosk con le barre visibili che un kiosk coperto da un overlay che non si
  // chiude e impedisce di timbrare.
  const [fullscreenBlocked, setFullscreenBlocked] = useState(false);
  // Dopo un reload di aggiornamento il fullscreen è perso per causa nostra:
  // niente overlay bloccante, il primo tocco (es. su Entrata/Uscita) lo ripristina.
  const [relockPending, setRelockPending] = useState(() => isRelockPending());
  // Dispositivi censiti dall'admin, tra cui scegliere in fase di configurazione
  const [availableDevices, setAvailableDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesLoadError, setDevicesLoadError] = useState('');
  // Ultima interazione e stato del kiosk, letti dal controllo aggiornamenti
  const lastInteractionRef = useRef(Date.now());
  const kioskBusyRef = useRef(false);

  // Funzione per mostrare notifiche migliorate in modalità kiosk
  const showNotification = (message, type = 'info', customSize = null) => {
    // In modalità kiosk, usa sempre dimensioni grandi per massima visibilità
    let size = customSize || 'large';
    
    // Per messaggi di sistema (setup, errori configurazione) usa dimensioni normali
    if (type === 'warning' && message.includes('setup')) {
      size = 'normal';
    }

    setNotification({
      show: true,
      message,
      type,
      size
    });
  };

  // Funzione per chiudere notifiche
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // 🔒 Blocco kiosk a schermo intero
  // Attivato SEMPRE al mount, non solo dopo la configurazione del dispositivo:
  // prima il fullscreen veniva chiesto una volta sola in authenticateDevice,
  // quindi dopo ogni riavvio o reload il tablet restava con le barre di sistema
  // Android visibili e bastava un tocco accidentale per uscire dalla schermata.
  useEffect(() => {
    const dispose = activateKioskLock({
      onLockChange: (locked) => {
        setIsLocked(locked);
        // Se torniamo bloccati, azzeriamo lo stato di "fullscreen impossibile":
        // il dispositivo evidentemente ce la fa.
        if (locked) {
          setFullscreenBlocked(false);
          clearRelockPending();
          setRelockPending(false);
        }
      }
    });

    return () => {
      dispose();
    };
  }, []);

  // 👆 Ogni tocco sul kiosk: registra l'attività (per rimandare gli
  // aggiornamenti) e, se il fullscreen è caduto, lo richiede. Ascoltiamo `click`
  // in fase di cattura perché è un evento che concede la "user activation"
  // richiesta dal browser, e non fermiamo l'evento: lo stesso tocco su
  // Entrata/Uscita riporta a schermo intero E avvia la timbratura.
  useEffect(() => {
    const handleInteraction = (e) => {
      lastInteractionRef.current = Date.now();
      // Il tocco sull'overlay di blocco richiede già il fullscreen da sé: una
      // seconda richiesta concorrente può fallire e far credere che il
      // dispositivo non supporti lo schermo intero.
      if (e.target?.closest?.('.kiosk-lock-overlay')) return;
      if (!isKioskLocked()) {
        requestKioskFullscreen();
      }
    };
    const markActivity = () => {
      lastInteractionRef.current = Date.now();
    };

    document.addEventListener('click', handleInteraction, true);
    document.addEventListener('touchstart', markActivity, { capture: true, passive: true });
    document.addEventListener('keydown', markActivity, true);

    return () => {
      document.removeEventListener('click', handleInteraction, true);
      document.removeEventListener('touchstart', markActivity, { capture: true, passive: true });
      document.removeEventListener('keydown', markActivity, true);
    };
  }, []);

  // Inizializzazione dell'app kiosk
  useEffect(() => {
    // Controlla se siamo in modalità kiosk
    const urlParams = new URLSearchParams(window.location.search);
    const kioskMode = urlParams.get('kiosk') || localStorage.getItem('kiosk_mode');
    
    if (kioskMode === 'true') {
      initializeKioskMode();
    }
    
    // Monitora stato connessione
    const handleOnline = () => {
      setConnectionStatus('online');
      showNotification(
        '✅ CONNESSIONE RIPRISTINATA\n🌐 Il dispositivo è di nuovo online',
        'success',
        'large'
      );
    };
    
    const handleOffline = () => {
      setConnectionStatus('offline');
      showNotification(
        '⚠️ CONNESSIONE PERSA\n📡 Dispositivo offline - Alcune funzioni potrebbero non funzionare',
        'warning',
        'large'
      );
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Monitora livello batteria (se supportato)
    if ('getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        const level = Math.round(battery.level * 100);
        setBatteryLevel(level);
        
        // Avvisa se batteria bassa
        if (level <= 20) {
          showNotification(
            `🔋 BATTERIA BASSA (${level}%)\n⚡ Collegare il dispositivo alla corrente`,
            'warning',
            'large'
          );
        }
        
        battery.addEventListener('levelchange', () => {
          const newLevel = Math.round(battery.level * 100);
          setBatteryLevel(newLevel);
          
          // Avvisi batteria critici
          if (newLevel <= 10 && level > 10) {
            showNotification(
              `🚨 BATTERIA CRITICA (${newLevel}%)\n⚡ COLLEGARE IMMEDIATAMENTE ALLA CORRENTE`,
              'error',
              'large'
            );
          } else if (newLevel <= 20 && level > 20) {
            showNotification(
              `🔋 BATTERIA BASSA (${newLevel}%)\n⚡ Collegare il dispositivo alla corrente`,
              'warning',
              'large'
            );
          }
        });
      });
    }
    
    // Event listener per il codice di emergenza
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowEmergencyInput(true);
        e.preventDefault();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('keydown', handleKeyDown);
      
      if (tapTimer) {
        clearTimeout(tapTimer);
      }
    };
  }, []);

  // Il controllo aggiornamenti gira in un effect montato una sola volta: gli
  // passiamo lo stato "occupato" tramite ref invece che come dipendenza.
  useEffect(() => {
    kioskBusyRef.current = notification.show || showEmergencyInput;
  }, [notification.show, showEmergencyInput]);

  // 🔄 Auto-aggiornamento kiosk
  // Un tablet sempre acceso non ricarica mai la pagina da solo, quindi senza
  // questo controllo non applicherebbe i nuovi deploy. Interroghiamo a
  // intervalli regolari public/version.json (servito senza cache, vedi
  // firebase.json) e, se il buildTimestamp remoto è più recente di quello
  // "cotto" in questo bundle, ricarichiamo per caricare la nuova versione.
  //
  // Regole per NON far uscire il tablet dalla modalità kiosk:
  // - niente clearAllCaches(): svuotava localStorage e cancellava kiosk_mode e
  //   l'ID del dispositivo. Scartiamo solo la Cache API (clearBuildCaches);
  // - il reload parte solo con il kiosk inattivo da UPDATE_IDLE_MS, mai mentre
  //   qualcuno timbra o legge una notifica;
  // - prima del reload segniamo `kiosk_relock_pending`, così al riavvio non
  //   copriamo lo scanner con l'overlay: il primo tocco ripristina il fullscreen;
  // - al massimo MAX_UPDATE_ATTEMPTS reload per build, contro i loop se il
  //   server continua a servire il bundle vecchio.
  useEffect(() => {
    let cancelled = false;
    let pendingTarget = null;
    let applying = false;

    clearUpdateAttemptsIfUpToDate(BUILD_TIMESTAMP);

    const checkForNewVersion = async () => {
      // Se offline, salta: riproveremo al prossimo intervallo
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;

        const data = await res.json();
        const serverTimestamp = Number(data.buildTimestamp);
        if (!serverTimestamp || Number.isNaN(serverTimestamp)) return;

        if (serverTimestamp > BUILD_TIMESTAMP && !cancelled) {
          if (!canAttemptUpdate(serverTimestamp)) {
            console.warn(`⚠️ [KIOSK-UPDATE] Build ${serverTimestamp} non caricata dopo più tentativi: rinuncio finché non esce un nuovo deploy`);
            return;
          }
          if (pendingTarget !== serverTimestamp) {
            console.log(`🔄 [KIOSK-UPDATE] Nuova versione rilevata (server=${serverTimestamp} > locale=${BUILD_TIMESTAMP}). Verrà applicata appena il kiosk è inattivo.`);
          }
          pendingTarget = serverTimestamp;
        }
      } catch (error) {
        // Offline o errore di rete: riproveremo al prossimo intervallo
        console.warn('⚠️ [KIOSK-UPDATE] Controllo versione non riuscito (riprovo più tardi):', error?.message || error);
      }
    };

    const applyUpdateIfIdle = async () => {
      if (!pendingTarget || applying || cancelled) return;
      if (kioskBusyRef.current) return;
      if (Date.now() - lastInteractionRef.current < UPDATE_IDLE_MS) return;

      applying = true;
      console.log('🔄 [KIOSK-UPDATE] Kiosk inattivo: applico l\'aggiornamento');
      recordUpdateAttempt(pendingTarget);
      markRelockPending();
      await clearBuildCaches();
      // Indispensabile: senza questo la guardia beforeunload del blocco kiosk
      // bloccherebbe il reload e i tablet non prenderebbero mai i nuovi deploy.
      allowNextUnload();
      window.location.reload();
    };

    // Un primo controllo poco dopo l'avvio, poi a intervalli regolari
    const initialTimeout = setTimeout(checkForNewVersion, 30 * 1000);
    const interval = setInterval(checkForNewVersion, VERSION_CHECK_INTERVAL_MS);
    const idleInterval = setInterval(applyUpdateIfIdle, UPDATE_IDLE_CHECK_MS);

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(interval);
      clearInterval(idleInterval);
    };
  }, []);

  // Inizializza modalità kiosk
  // NB: wake lock, blocco scroll/zoom/tastiera e fullscreen sono ora gestiti da
  // utils/kioskLock.js (attivato nell'effect qui sopra), che li rimuove anche
  // correttamente allo smontaggio. Qui resta solo il caricamento del dispositivo.
  const initializeKioskMode = () => {
    // Rimuovi barre di scorrimento
    document.body.style.overflow = 'hidden';

    // Carica info dispositivo dal localStorage
    const savedDeviceId = localStorage.getItem('kiosk_device_id');
    const savedDeviceName = localStorage.getItem('kiosk_device_name');
    
    if (savedDeviceId && savedDeviceName) {
      setDeviceId(savedDeviceId);
      setDeviceName(savedDeviceName);
      setIsAuthenticated(true);

      // Dopo un aggiornamento automatico ripartiamo in silenzio: nessuna
      // notifica a tutto schermo davanti allo scanner.
      if (!isRelockPending()) {
        setTimeout(() => {
          showNotification(
            `✅ DISPOSITIVO KIOSK INIZIALIZZATO\n📱 ${savedDeviceName}\n🆔 ID: ${savedDeviceId}`,
            'success',
            'normal'
          );
        }, 1000);
      }

      syncDeviceWithAdminRegistry(savedDeviceId);
    }
  };

  // Riallinea il tablet al documento del dispositivo censito dall'admin: il nome
  // mostrato è quello deciso dall'admin (anche se rinominato dopo la
  // configurazione) e comunichiamo la build in uso. Se l'admin ha eliminato il
  // dispositivo, il tablet torna alla configurazione. In caso di errore di rete
  // manteniamo l'identità salvata: meglio timbrare che bloccarsi offline.
  const syncDeviceWithAdminRegistry = async (savedDeviceId) => {
    try {
      const device = await timekeepingService.getKioskDevice(savedDeviceId);

      if (!device) {
        console.warn(`⚠️ [KIOSK] Dispositivo ${savedDeviceId} non più censito: richiesta nuova configurazione`);
        localStorage.removeItem('kiosk_device_id');
        localStorage.removeItem('kiosk_device_name');
        localStorage.removeItem('kiosk_auth_time');
        setIsAuthenticated(false);
        setDeviceId('');
        setDeviceName('');
        showNotification(
          '⚠️ DISPOSITIVO NON PIÙ CENSITO\n📝 Selezionare il dispositivo corretto dall\'elenco',
          'warning',
          'normal'
        );
        return;
      }

      if (device.deviceName !== localStorage.getItem('kiosk_device_name')) {
        localStorage.setItem('kiosk_device_name', device.deviceName);
      }
      setDeviceName(device.deviceName);

      await timekeepingService.reportKioskHeartbeat(savedDeviceId, BUILD_TIMESTAMP);
    } catch (error) {
      console.warn('⚠️ [KIOSK] Verifica dispositivo non riuscita (uso i dati salvati):', error?.message || error);
    }
  };

  // Carica l'elenco dei dispositivi censiti quando serve la configurazione
  const loadAvailableDevices = async () => {
    setDevicesLoading(true);
    setDevicesLoadError('');
    try {
      const devices = await timekeepingService.getRegisteredKioskDevices();
      setAvailableDevices(devices);
    } catch (error) {
      console.error('Errore caricamento dispositivi censiti:', error);
      setDevicesLoadError('Impossibile caricare l\'elenco dei dispositivi. Verificare la connessione e riprovare.');
    } finally {
      setDevicesLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      loadAvailableDevices();
    }
  }, [isAuthenticated]);

  // Entra in modalità fullscreen.
  // Chiamabile SOLO da un gesto utente (tap sull'overlay di blocco, pulsante di
  // configurazione): i browser rifiutano la richiesta se parte da un timer.
  // @param {boolean} fromUserGesture true se la chiamata nasce da un tocco reale.
  //        Solo in quel caso un fallimento significa davvero "questo dispositivo
  //        non supporta il fullscreen": un tentativo programmatico può fallire
  //        semplicemente perché manca il gesto, e in quel caso l'overlay deve
  //        restare visibile per raccoglierlo.
  const requestFullscreen = async (fromUserGesture = false) => {
    const locked = await requestKioskFullscreen();

    if (locked) {
      setFullscreenBlocked(false);
    } else if (fromUserGesture) {
      // Non insistiamo più: l'overlay sparisce e il kiosk resta usabile.
      setFullscreenBlocked(true);
      showNotification(
        '⚠️ FULLSCREEN NON DISPONIBILE\n📱 Attiva il "Blocco su schermo" di Android per proteggere il dispositivo',
        'warning',
        'normal'
      );
    }

    return locked;
  };

  // Tap sull'overlay di blocco: unico modo per rientrare a schermo intero
  const handleUnlockTap = () => {
    requestFullscreen(true);
  };

  // Gestisce i tap nell'angolo alto sinistro per l'emergenza
  const handleEmergencyTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const y = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    
    // Area di emergenza: angolo alto sinistro 100x100 pixel
    if (x <= 100 && y <= 100) {
      const newTapCount = tapCount + 1;
      setTapCount(newTapCount);
      
      if (tapTimer) {
        clearTimeout(tapTimer);
      }
      
      // Se raggiunge 5 tap, attiva l'emergenza
      if (newTapCount >= 5) {
        setShowEmergencyInput(true);
        setTapCount(0);
        setTapTimer(null);
        
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200, 100, 200]);
        }
        
        showNotification(
          '🚨 MODALITÀ EMERGENZA ATTIVATA\n🔐 Inserire codice di uscita',
          'warning',
          'large'
        );
        return;
      }
      
      const timer = setTimeout(() => {
        setTapCount(0);
        setTapTimer(null);
      }, 3000);
      
      setTapTimer(timer);
      
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Indicatore visivo
      const indicator = document.createElement('div');
      indicator.className = 'tap-indicator';
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        left: 20px;
        width: 60px;
        height: 60px;
        background: rgba(231, 76, 60, 0.8);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 1.2rem;
        z-index: 9999;
        animation: tap-pulse 0.5s ease-out;
        pointer-events: none;
      `;
      indicator.textContent = newTapCount;
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 500);
    }
  };

  const handleEmergencyLogout = async () => {
    if (emergencyCode === 'EXITSCANNER') {
      try {
        showNotification(
          '🔄 LOGOUT DI EMERGENZA IN CORSO...\n⏳ Attendere...',
          'info',
          'large'
        );

        // Sblocca il kiosk: rimuove fullscreen forzato, trap del tasto indietro,
        // guardia beforeunload e wake lock. Senza questo l'uscita d'emergenza
        // resterebbe intrappolata dalle stesse difese.
        deactivateKioskLock();
        allowNextUnload();

        // Logout dall'autenticazione
        if (auth.currentUser) {
          await signOut(auth);
        }

        // Rimuovi modalità kiosk
        localStorage.removeItem('kiosk_mode');
        localStorage.removeItem('kiosk_device_id');
        localStorage.removeItem('kiosk_device_name');
        localStorage.removeItem('kiosk_auth_time');
        
        // Esci da fullscreen
        exitKioskFullscreen();

        showNotification(
          '✅ LOGOUT COMPLETATO\n🔄 Reindirizzamento in corso...',
          'success',
          'large'
        );
        
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
        
      } catch (error) {
        console.error('Errore durante il logout di emergenza:', error);
        showNotification(
          '❌ ERRORE LOGOUT DI EMERGENZA\n📞 Contattare l\'assistenza',
          'error',
          'large'
        );
      }
    } else {
      showNotification(
        '❌ CODICE DI EMERGENZA NON VALIDO\n🔐 Riprovare con il codice corretto',
        'error',
        'normal'
      );
      setEmergencyCode('');
    }
  };

  // Autentica dispositivo kiosk
  const authenticateDevice = async () => {
    if (!deviceId || !authCode) {
      showNotification(
        '⚠️ CAMPI MANCANTI\n📝 Seleziona il dispositivo e inserisci il codice',
        'warning',
        'normal'
      );
      return;
    }
    
    if (authCode !== 'KIOSK2025') {
      showNotification(
        '❌ CODICE DI AUTENTICAZIONE NON VALIDO\n🔐 Verificare il codice e riprovare',
        'error',
        'normal'
      );
      return;
    }
    
    try {
      showNotification(
        '⏳ AUTENTICAZIONE IN CORSO...\n📱 Registrazione dispositivo...',
        'info',
        'normal'
      );

      // Il tablet si collega a un dispositivo censito dall'admin: ID, nome e
      // posizione arrivano da lì, non da ciò che si digita sul tablet.
      const device = await timekeepingService.bindKioskDevice(deviceId, {
        deviceType: 'kiosk',
        kioskMode: true,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        registeredAt: new Date().toISOString()
      });

      localStorage.setItem('kiosk_device_id', device.id);
      localStorage.setItem('kiosk_device_name', device.deviceName);
      localStorage.setItem('kiosk_auth_time', new Date().toISOString());

      setDeviceName(device.deviceName);
      setIsAuthenticated(true);
      timekeepingService.reportKioskHeartbeat(device.id, BUILD_TIMESTAMP);

      const warnings = [];
      if (!device.assignedLocation) warnings.push('⚠️ Nessuna posizione assegnata: chiedere all\'admin');
      if (!device.active) warnings.push('⏸️ Dispositivo in pausa: timbrature bloccate');

      showNotification(
        `✅ DISPOSITIVO AUTENTICATO\n📱 ${device.deviceName}\n🆔 ID: ${device.id}\n${warnings.length ? warnings.join('\n') : '🚀 Sistema pronto per l\'uso'}`,
        'success',
        'large'
      );
      
      // Attiva fullscreen subito: siamo ancora dentro la finestra di "user
      // activation" del tap sul pulsante di configurazione. Se scade, l'overlay
      // di blocco compare e raccoglie il tocco successivo.
      requestFullscreen();
      
    } catch (error) {
      console.error('Errore autenticazione:', error);
      showNotification(
        `❌ ERRORE AUTENTICAZIONE\n📝 ${error.message}\n🔄 Riprovare`,
        'error',
        'normal'
      );
    }
  };

  // Logout dispositivo (per manutenzione)
  const logoutDevice = () => {
    const confirmLogout = window.confirm('Sei sicuro di voler disconnettere questo dispositivo?');
    if (confirmLogout) {
      localStorage.removeItem('kiosk_device_id');
      localStorage.removeItem('kiosk_device_name');
      localStorage.removeItem('kiosk_auth_time');
      setIsAuthenticated(false);
      setDeviceId('');
      setDeviceName('');
      setAuthCode('');
      
      showNotification(
        '🔓 DISPOSITIVO DISCONNESSO\n⚙️ Modalità manutenzione attivata',
        'info',
        'normal'
      );
    }
  };

  // Ottieni ora corrente
  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Se non autenticato, mostra schermata di setup
  if (!isAuthenticated) {
    return (
      <div 
        className="kiosk-setup"
        onClick={handleEmergencyTap}
        onTouchStart={handleEmergencyTap}
      >
        <div className="setup-container">
          <div className="setup-header">
            <h1>🔒 Configurazione Dispositivo Kiosk</h1>
            <p>Configura questo dispositivo per le timbrature</p>
          </div>
          
          <div className="setup-form">
            {/* ID e nome NON si digitano: si sceglie tra i dispositivi creati
                dall'admin (tab Dispositivi), così la posizione assegnata resta
                legata al tablet e non nascono dispositivi duplicati. */}
            <div className="form-group">
              <label htmlFor="deviceId">Dispositivo</label>
              {devicesLoading ? (
                <p>Caricamento dispositivi...</p>
              ) : devicesLoadError ? (
                <div>
                  <p style={{ color: '#e74c3c' }}>{devicesLoadError}</p>
                  <button type="button" className="setup-button" onClick={loadAvailableDevices}>
                    Riprova
                  </button>
                </div>
              ) : availableDevices.length === 0 ? (
                <p style={{ color: '#e74c3c' }}>
                  Nessun dispositivo censito. Crearlo prima dal pannello amministratore (Dispositivi).
                </p>
              ) : (
                <select
                  id="deviceId"
                  value={deviceId}
                  onChange={(e) => {
                    const selected = availableDevices.find(d => d.id === e.target.value);
                    setDeviceId(e.target.value);
                    setDeviceName(selected ? selected.deviceName : '');
                  }}
                >
                  <option value="">-- Seleziona il dispositivo --</option>
                  {availableDevices.map(device => (
                    <option key={device.id} value={device.id}>
                      {device.deviceName} ({device.id}){!device.active ? ' - IN PAUSA' : ''}{!device.assignedLocation ? ' - senza posizione' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="authCode">Codice Autorizzazione</label>
              <input
                type="password"
                id="authCode"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                placeholder="Inserisci codice di autorizzazione"
                autoComplete="off"
              />
            </div>
            
            <button 
              className="setup-button"
              onClick={authenticateDevice}
            >
              Configura Dispositivo
            </button>
          </div>
          
          {/* Notifiche per setup */}
          <Notification
            message={notification.message}
            isVisible={notification.show}
            onClose={closeNotification}
            type={notification.type}
            size={notification.size}
            duration={notification.type === 'success' ? 8000 : 6000}
            playSound={true}
            enableVibration={true}
          />
        </div>
        
        {/* Input di emergenza per il logout */}
        {showEmergencyInput && (
          <div className="emergency-overlay">
            <div className="emergency-form">
              <h3>🚨 Logout di Emergenza</h3>
              <p>Inserisci il codice di emergenza per uscire dalla modalità kiosk:</p>
              <input
                type="password"
                value={emergencyCode}
                onChange={(e) => setEmergencyCode(e.target.value)}
                placeholder="Inserisci codice di emergenza"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEmergencyLogout();
                  }
                }}
              />
              <div className="emergency-buttons">
                <button onClick={handleEmergencyLogout} className="emergency-confirm">
                  Conferma Logout
                </button>
                <button 
                  onClick={() => {
                    setShowEmergencyInput(false);
                    setEmergencyCode('');
                    setTapCount(0);
                    if (tapTimer) {
                      clearTimeout(tapTimer);
                      setTapTimer(null);
                    }
                  }} 
                  className="emergency-cancel"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Indicatore visivo area tap di emergenza */}
        {tapCount > 0 && (
          <div className="emergency-tap-zone">
            <div className="tap-progress">
              <div className="tap-counter">{tapCount}/5</div>
              <div className="tap-progress-bar">
                <div
                  className="tap-progress-fill"
                  style={{ width: `${(tapCount / 5) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Blocco a schermo intero: nascosto mentre si inserisce il codice
            d'emergenza, altrimenti coprirebbe il form dell'admin */}
        <KioskLockOverlay
          visible={!isLocked && !fullscreenBlocked && !relockPending && !showEmergencyInput}
          onUnlock={handleUnlockTap}
          onCornerTap={handleEmergencyTap}
        />
      </div>
    );
  }

  return (
    <div 
      className="kiosk-app" 
      onClick={handleEmergencyTap}
      onTouchStart={handleEmergencyTap}
    >
      {/* Container principale per contenuto */}
      <div className="kiosk-main-content">
        {/* Notifiche sempre visibili */}
        <Notification
          message={notification.message}
          isVisible={notification.show}
          onClose={closeNotification}
          type={notification.type}
          size={notification.size}
          duration={notification.type === 'success' || notification.type === 'error' ? 10000 : 7000}
          playSound={true}
          enableVibration={true}
        />

        {/* Scanner principale */}
        <div className="kiosk-scanner-container">
          <TimekeepingScanner 
            isAdmin={false}
            deviceId={deviceId}
            kioskMode={true}
          />
        </div>
      </div>

      {/* Header spostato in fondo con dimensioni ridotte */}
      <div className="kiosk-footer">
        <div className="device-info-compact">
          <div className="device-name-compact">
            📱 {deviceName}
          </div>
          <div className="device-status-compact">
            <span className="time-compact">{getCurrentTime()}</span>
            <span className={`connection-compact ${connectionStatus}`}>
              {connectionStatus === 'online' ? '🟢' : '🔴'}
            </span>
            {batteryLevel !== null && (
              <span className={`battery-compact ${batteryLevel <= 20 ? 'battery-low' : ''}`}>
                🔋 {batteryLevel}%
              </span>
            )}
          </div>
        </div>
        
        {/* Pulsante nascosto per logout (doppio tap + hold) */}
        <div 
          className="hidden-logout-compact"
          onDoubleClick={(e) => {
            setTimeout(() => {
              const secret = prompt('Inserisci codice di manutenzione:');
              if (secret === 'MANUTENZIONE2025') {
                logoutDevice();
              }
            }, 1000);
          }}
        >
          ⚙️
        </div>
      </div>
      
      {/* Input di emergenza per il logout */}
      {showEmergencyInput && (
        <div className="emergency-overlay">
          <div className="emergency-form">
            <h3>🚨 Logout di Emergenza</h3>
            <p>Inserisci il codice di emergenza per uscire dalla modalità kiosk:</p>
            <input
              type="password"
              value={emergencyCode}
              onChange={(e) => setEmergencyCode(e.target.value)}
              placeholder="Inserisci codice di emergenza"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleEmergencyLogout();
                }
              }}
            />
            <div className="emergency-buttons">
              <button onClick={handleEmergencyLogout} className="emergency-confirm">
                Conferma Logout
              </button>
              <button 
                onClick={() => {
                  setShowEmergencyInput(false);
                  setEmergencyCode('');
                  setTapCount(0);
                  if (tapTimer) {
                    clearTimeout(tapTimer);
                    setTapTimer(null);
                  }
                }} 
                className="emergency-cancel"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Overlay per prevenire interazioni indesiderate */}
      <div className="interaction-overlay" />

      {/* Blocco a schermo intero: copre lo scanner finché il kiosk non è
          tornato a schermo intero, così non si può timbrare da un dispositivo
          uscito accidentalmente dalla modalità kiosk */}
      <KioskLockOverlay
        visible={!isLocked && !fullscreenBlocked && !relockPending && !showEmergencyInput}
        deviceName={deviceName}
        onUnlock={handleUnlockTap}
        onCornerTap={handleEmergencyTap}
      />

      {/* Dopo un aggiornamento automatico: avviso discreto che non blocca lo
          scanner. Il primo tocco (anche su Entrata/Uscita) torna a schermo intero. */}
      {!isLocked && !fullscreenBlocked && relockPending && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: '6px 12px',
          background: 'rgba(44, 62, 80, 0.85)',
          color: 'white',
          fontSize: '0.9rem',
          textAlign: 'center',
          zIndex: 9000,
          pointerEvents: 'none'
        }}>
          🔄 Aggiornato alla nuova versione · tocca lo schermo per tornare a schermo intero
        </div>
      )}

      {/* Stili CSS per indicatori batteria */}
      <style jsx>{`
        .battery-low {
          color: #ff4444 !important;
          animation: battery-blink 2s infinite;
        }
        
        @keyframes battery-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
        
        .tap-indicator {
          animation: tap-pulse 0.5s ease-out !important;
        }
        
        @keyframes tap-pulse {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default KioskApp;